import type { ViewComponent } from './components/index.h'

import { combine, createStore, sample } from 'effector'
import { isType } from 'variant'
import { register } from '~/core/repository'
import { appearanceFeature } from '~/features/appearance'
import { $isBannersLoading } from './banners/model'
import { $isRowsLoading } from './components/components-request'
import {
  $isComponentsLoadFailed,
  $isComponentsLoaded,
} from './components/dynamic-components'
import { Empty, Failed, Ready } from './components/index.h'
import {
  $loadQueue,
  $structure,
  initModule,
  leaveModule,
} from './components/model'
import { $viewComponents } from './view-components'

/**
 * Skeleton vs empty-state for Home / dynamic modules.
 * Route render runs in parallel with init, so getComponentsFx.pending is still
 * false on first paint. Treat an unsettled first page as loading so an empty
 * PageWrapper does not match `.page-wrapper:empty`.
 */
export const $isModuleContentLoading = combine(
  $viewComponents,
  $isRowsLoading,
  $isComponentsLoaded,
  $isComponentsLoadFailed,
  $isBannersLoading,
  (rows, pending, loaded, failed, bannersPending) =>
    bannersPending || (!rows.length && !failed && (pending || !loaded))
)

export const $dynamicModulePageActive = createStore(false)
  .on(initModule, () => true)
  .reset(leaveModule)

function isVisibleMixedItem(item: unknown): boolean {
  return (
    typeof item === 'object' &&
    item !== null &&
    (!('watched' in item) || (item as { watched?: unknown }).watched !== true)
  )
}

function hasVisibleViewContent(view: ViewComponent): boolean {
  const content = view.content

  if (Array.isArray(content)) {
    if (view.appearance.contentType === 'MIXED') {
      return content.some(isVisibleMixedItem)
    }

    return content.length > 0
  }

  if (content && typeof content === 'object' && 'bannerBundles' in content) {
    const bundles = (content as { bannerBundles?: unknown }).bannerBundles
    return Array.isArray(bundles) && bundles.length > 0
  }

  return false
}

export const $hasVisibleDynamicShelf = $viewComponents.map((components) =>
  components.some(hasVisibleViewContent)
)

export const $allStructureTerminal = $structure.map((structure) => {
  if (!structure || structure.size === 0) return true

  for (const [, { data }] of structure) {
    if (!isType(data, Ready) && !isType(data, Failed)) return false
  }

  return true
})

export const $shouldShowGlobalEmptyState = combine(
  $dynamicModulePageActive,
  $isModuleContentLoading,
  $allStructureTerminal,
  $hasVisibleDynamicShelf,
  (pageActive, loading, terminal, hasVisible) =>
    pageActive && !loading && terminal && !hasVisible
)

const $identifier = appearanceFeature.$currentIdentifier

sample({
  clock: [
    $structure,
    $isModuleContentLoading,
    $hasVisibleDynamicShelf,
    $isComponentsLoaded,
    $isComponentsLoadFailed,
    $dynamicModulePageActive,
  ],
  source: {
    structure: $structure,
    queue: $loadQueue,
    identifier: $identifier,
    loading: $isModuleContentLoading,
    hasVisible: $hasVisibleDynamicShelf,
    loaded: $isComponentsLoaded,
    failed: $isComponentsLoadFailed,
    pageActive: $dynamicModulePageActive,
  },
  filter: ({ loading, hasVisible, loaded, failed, structure, pageActive }) =>
    pageActive &&
    !loading &&
    !hasVisible &&
    (loaded || failed) &&
    Boolean(
      structure &&
      [...structure.values()].some(({ data }) => isType(data, Empty))
    ),
  fn: ({ structure, queue, identifier }) => {
    if (!structure || !identifier) return queue

    const queuedKeys = new Set(
      queue.map(
        ({ identifier: queuedIdentifier, component }) =>
          `${queuedIdentifier}:${component.id}`
      )
    )
    const updatedQueue = [...queue]

    for (const [, { data, desc }] of structure) {
      const queueKey = `${identifier}:${desc.id}`

      if (isType(data, Empty) && !queuedKeys.has(queueKey)) {
        queuedKeys.add(queueKey)
        updatedQueue.push({ identifier, component: desc })
      }
    }

    return updatedQueue
  },
  target: $loadQueue,
})

register(
  $shouldShowGlobalEmptyState,
  '$widgets/DynamicModule/shouldShowGlobalEmptyState'
)
